/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import FeatureEnablement from '../../environment/FeatureEnablement';
import WebPlayer from './WebPlayer';
import SDK from '../SDK';
import ShakaPlayer from './ShakaPlayer';
import HlsPlayer from './HlsPlayer';
import IDisposable from '../../lang/IDisposable';
import {
  ChunkedStreamingOptions,
  ChunkedStreamingStatistics
} from './StreamTypes';
import {ShakaPlayerType} from './IShakaPlayer';
import {HlsPlayerType} from './IHlsPlayer';
import {WebPlayerType} from './IWebPlayer';

export class ChunkedStreaming {
  private static _pending = false;
  private static _hlsPlayer: HlsPlayerType;
  private static _phenixWebPlayer: WebPlayerType;
  private static _shaka: ShakaPlayerType;
  private static _options: ChunkedStreamingOptions;
  private static _player: ShakaPlayer | WebPlayer | HlsPlayer;

  static get hlsPlayer(): HlsPlayerType {
    return this._hlsPlayer;
  }

  static get phenixWebPlayer(): WebPlayerType {
    return this._phenixWebPlayer;
  }

  static get shaka(): ShakaPlayerType {
    return this._shaka;
  }

  static async start(videoElement: HTMLVideoElement, streamId: string, offerSdp: string, options: ChunkedStreamingOptions, handleStreamFailure: Function): Promise<IDisposable> {
    this._options = options;

    if (this._hlsPlayer || this._shaka || this._phenixWebPlayer) {
      return this.loaded(videoElement, streamId, offerSdp, handleStreamFailure);
    }

    if (!SDK.webPlayerLoader && !SDK.shakaPlayerLoader && !SDK.hlsJsLoader) {
      throw new Error('Unsupported-feature, add stream player');
    }

    return new Promise<IDisposable>(resolve => {
      if (SDK.hlsJsLoader) {
        this._pending = true;
        SDK.hlsJsLoader((player: HlsPlayerType): void => {
          this._hlsPlayer = player;

          if (this._pending) {
            this._pending = false;

            resolve(this.loaded(videoElement, streamId, offerSdp, handleStreamFailure));
          }
        });
      }

      if (SDK.webPlayerLoader) {
        this._pending = true;
        SDK.webPlayerLoader((player: WebPlayerType): void => {
          this._phenixWebPlayer = player;

          if (this._pending) {
            this._pending = false;

            resolve(this.loaded(videoElement, streamId, offerSdp, handleStreamFailure));
          }
        });
      }

      if (SDK.shakaPlayerLoader) {
        this._pending = true;
        SDK.shakaPlayerLoader((player: ShakaPlayerType): void => {
          this._shaka = player;

          if (this._pending) {
            this._pending = false;

            resolve(this.loaded(videoElement, streamId, offerSdp, handleStreamFailure));
          }
        });
      }
    });
  }

  static getStats(): ChunkedStreamingStatistics | null {
    if (this._player) {
      return this._player.getStats();
    }

    return null;
  }

  private static async play(Player: typeof WebPlayer | typeof ShakaPlayer | typeof HlsPlayer, videoElement: HTMLVideoElement, streamId: string, offerSdp: string, options: ChunkedStreamingOptions, handleStreamFailure: Function): Promise<IDisposable> {
    const dashManifestUrlMatch = offerSdp.match(/a=x-playlist:([^\n]*[.]mpd\??[^\s]*)/m);
    const hlsManifestUrlMatch = offerSdp.match(/a=x-playlist:([^\n]*[.]m3u8\??[^\s]*)/m);
    const manifestUrl = dashManifestUrlMatch && dashManifestUrlMatch[1] ? dashManifestUrlMatch[1] : '';
    const playlistUrl = hlsManifestUrlMatch && hlsManifestUrlMatch[1] ? hlsManifestUrlMatch[1] : '';

    if (playlistUrl) {
      if (!this._phenixWebPlayer || FeatureEnablement.shouldUseNativeHls) {
        this._player = new Player(videoElement, 'hls', streamId, playlistUrl, options, handleStreamFailure);

        this._player.start();

        return this._player;
      }
    }

    if (manifestUrl) {
      if (!this._hlsPlayer && !(this._phenixWebPlayer && FeatureEnablement.shouldUseNativeHls)) {
        this._player = new Player(videoElement, 'dash', streamId, manifestUrl, options, handleStreamFailure);

        this._player.start();

        return this._player;
      }
    }

    if (manifestUrl || playlistUrl) {
      throw new Error(`[${streamId}] Cannot play [${playlistUrl ? 'HLS' : 'DASH'}] stream with included player`);
    }

    throw new Error('No stream playlist found');
  }

  private static loaded(videoElement: HTMLVideoElement, streamId: string, offerSdp: string, handleStreamFailure: Function): Promise<IDisposable> {
    if (this._hlsPlayer) {
      return this.play(HlsPlayer, videoElement, streamId, offerSdp, {
        ...this._options,
        preferNative: FeatureEnablement.shouldUseNativeHls
      }, handleStreamFailure);
    }

    if (this._phenixWebPlayer) {
      return this.play(WebPlayer, videoElement, streamId, offerSdp, {
        ...this._options,
        preferNative: FeatureEnablement.shouldUseNativeHls
      }, handleStreamFailure);
    }

    return this.play(ShakaPlayer, videoElement, streamId, offerSdp, {
      ...this._options,
      preferNative: FeatureEnablement.shouldUseNativeHls
    }, handleStreamFailure);
  }
}